import React, { createContext, useContext, useState } from 'react';
import ContactModal from '../components/ContactModal';
import CareerModal from '../components/CareerModal';

// Modal types
export const MODAL_TYPES = {
  CONTACT: 'contact',
  CAREER: 'career'
};

// Create context
const ModalContext = createContext();

// Hook to use modal context
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

// Modal Provider Component
export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    props: {}
  });

  // Open modal function
  const openModal = (type, props = {}) => {
    console.log('openModal called:', { type, props });
    setModalState({
      isOpen: true,
      type,
      props
    });
    console.log('Modal state set to:', { isOpen: true, type, props });
  };

  // Close modal function
  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      props: {}
    });
  };

  // Convenience functions for specific modals
  const openContactModal = (serviceContext = '', buttonContext = '') => {
    console.log('openContactModal called:', { serviceContext, buttonContext });
    openModal(MODAL_TYPES.CONTACT, {
      serviceContext,
      buttonContext
    });
  };

  const openCareerModal = (positionTitle = '', positionId = '') => {
    openModal(MODAL_TYPES.CAREER, {
      positionTitle,
      positionId
    });
  };

  // Render the appropriate modal
  const renderModal = () => {
    if (!modalState.isOpen) return null;

    switch (modalState.type) {
      case MODAL_TYPES.CONTACT:
        return (
          <ContactModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            serviceContext={modalState.props.serviceContext}
            buttonContext={modalState.props.buttonContext}
          />
        );
      
      case MODAL_TYPES.CAREER:
        return (
          <CareerModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            positionTitle={modalState.props.positionTitle}
            positionId={modalState.props.positionId}
          />
        );
      
      default:
        return null;
    }
  };

  // Context value
  const value = {
    isOpen: modalState.isOpen,
    modalType: modalState.type,
    openModal,
    closeModal,
    openContactModal,
    openCareerModal
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      {renderModal()}
    </ModalContext.Provider>
  );
};

// HOC for components that need modal functionality
export const withModal = (Component) => {
  return (props) => {
    const modalProps = useModal();
    return <Component {...props} modal={modalProps} />;
  };
};

export default ModalContext; 